import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fetchMyGigs, fetchOrders, deleteGig, pauseGig } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fileUrl } from "@/lib/api";
import { Plus, Package, DollarSign, Star, Trash2, Pencil, Pause, Play } from "lucide-react";
import { toast } from "sonner";
import VerificationCard from "@/components/VerificationCard";

export default function DashboardPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [gigs, setGigs] = useState([]);
  const [buyerOrders, setBuyerOrders] = useState([]);
  const [sellerOrders, setSellerOrders] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  const loadAll = () => {
    if (!user) return;
    fetchMyGigs().then(setGigs).catch(() => {});
    fetchOrders("buyer").then(setBuyerOrders).catch(() => {});
    fetchOrders("seller").then(setSellerOrders).catch(() => {});
  };
  useEffect(loadAll, [user]);

  if (loading || !user) return <div className="p-16 text-center">Loading…</div>;

  const totalEarnings = sellerOrders
    .filter((o) => o.status === "completed")
    .reduce((s, o) => s + o.package_price, 0);
  const activeSellerOrders = sellerOrders.filter((o) => o.status === "in_progress" || o.status === "delivered").length;

  const handlePause = async (g) => {
    try {
      await pauseGig(g.gig_id, !g.paused);
      toast.success(g.paused ? "Gig is live again" : "Gig paused — hidden from buyers");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Deactivate this gig?")) return;
    try { await deleteGig(id); toast.success("Gig deactivated"); loadAll(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-end justify-between mb-8 border-b border-black/10 pb-6">
        <div>
          <div className="overline text-primary mb-2">◆ CONTROL ROOM</div>
          <h1 className="text-4xl font-bold tracking-tight">Welcome back, {user.name.split(" ")[0]}</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/earnings" data-testid="dash-earnings-btn" className="border border-foreground px-5 py-2.5 mono text-xs hover:bg-foreground hover:text-background flex items-center gap-2">
            EARNINGS
          </Link>
          <Link to="/dashboard/new-gig" data-testid="dash-new-gig-btn" className="bg-primary text-primary-foreground px-5 py-2.5 mono text-xs hover:bg-primary/90 flex items-center gap-2">
            <Plus className="w-4 h-4"/> POST A GIG
          </Link>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-active-gigs" icon={<Package className="w-5 h-5"/>} label="Active gigs" value={gigs.length}/>
        <StatCard testid="stat-earnings" icon={<DollarSign className="w-5 h-5"/>} label="Total earnings" value={`$${totalEarnings.toFixed(0)}`}/>
        <StatCard testid="stat-active-orders" icon={<Package className="w-5 h-5"/>} label="Active seller orders" value={activeSellerOrders}/>
        <StatCard testid="stat-rating" icon={<Star className="w-5 h-5"/>} label="Rating" value={user.rating_avg?.toFixed(1) || "—"}/>
      </div>

      <div className="mb-10">
        <VerificationCard/>
      </div>

      <Tabs defaultValue="gigs">
        <TabsList className="rounded-none border border-foreground/15 bg-transparent p-0 h-auto">
          <TabsTrigger data-testid="tab-my-gigs" value="gigs" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-5 py-2.5">My gigs</TabsTrigger>
          <TabsTrigger data-testid="tab-selling" value="selling" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-5 py-2.5">Selling</TabsTrigger>
          <TabsTrigger data-testid="tab-buying" value="buying" className="rounded-none data-[state=active]:bg-foreground data-[state=active]:text-background px-5 py-2.5">Buying</TabsTrigger>
        </TabsList>

        <TabsContent value="gigs" className="pt-6">
          {gigs.length === 0 ? (
            <EmptyState msg="You haven't posted any gigs yet." cta={<Link to="/dashboard/new-gig" className="mono text-xs underline">POST YOUR FIRST GIG</Link>}/>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {gigs.map((g, i) => (
                <div key={g.gig_id} data-testid={`my-gig-${i}`} className="border border-foreground/15 bg-card group">
                  <Link to={`/gig/${g.gig_id}`} className="relative block aspect-video overflow-hidden bg-muted">
                    <img src={fileUrl(g.cover_image)} className={`w-full h-full object-cover transition-opacity ${g.paused ? "opacity-40 grayscale" : ""}`} alt=""/>
                    {g.paused && <span data-testid={`paused-badge-${g.gig_id}`} className="absolute top-3 left-3 bg-foreground text-background mono text-[10px] px-2 py-1 flex items-center gap-1"><Pause className="w-3 h-3"/> PAUSED</span>}
                  </Link>
                  <div className="p-4">
                    <div className="font-medium line-clamp-2 mb-2">{g.title}</div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground mono">
                      <span>{g.orders_count} orders</span>
                      <span>${Math.min(...g.packages.map(p => p.price))}+</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t border-foreground/10 mt-3">
                      <Link
                        to={`/dashboard/edit-gig/${g.gig_id}`}
                        data-testid={`edit-gig-${g.gig_id}`}
                        className="text-xs mono flex items-center gap-1 hover:text-primary transition-colors"
                      ><Pencil className="w-3 h-3"/> EDIT</Link>
                      <button
                        data-testid={`pause-gig-${g.gig_id}`}
                        onClick={() => handlePause(g)}
                        className="text-xs mono flex items-center gap-1 hover:text-primary transition-colors"
                      >{g.paused ? <><Play className="w-3 h-3"/> RESUME</> : <><Pause className="w-3 h-3"/> PAUSE</>}</button>
                      <button
                        data-testid={`delete-gig-${g.gig_id}`}
                        onClick={() => handleDelete(g.gig_id)}
                        className="text-xs mono flex items-center gap-1 text-destructive hover:underline"
                      ><Trash2 className="w-3 h-3"/> DEACTIVATE</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="selling" className="pt-6">
          <OrdersTable orders={sellerOrders} role="seller"/>
        </TabsContent>
        <TabsContent value="buying" className="pt-6">
          <OrdersTable orders={buyerOrders} role="buyer"/>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="border border-foreground/15 p-5 bg-card">
      <div className="flex items-center justify-between mb-4 text-muted-foreground">{icon}</div>
      <div className="mono text-3xl font-bold">{value}</div>
      <div className="overline mt-1">{label}</div>
    </div>
  );
}

function EmptyState({ msg, cta }) {
  return (
    <div className="border border-dashed border-foreground/20 p-12 text-center">
      <div className="text-sm text-muted-foreground mb-3">{msg}</div>
      {cta}
    </div>
  );
}

function OrdersTable({ orders, role }) {
  if (orders.length === 0) return <EmptyState msg={`No ${role} orders yet.`}/>;
  return (
    <div className="border border-foreground/15">
      <div className="grid grid-cols-[1fr_120px_120px_120px_100px] gap-4 px-5 py-3 border-b border-foreground/15 bg-muted overline">
        <div>◆ Gig</div>
        <div>{role === "seller" ? "Buyer" : "Seller"}</div>
        <div>Price</div>
        <div>Status</div>
        <div>Action</div>
      </div>
      {orders.map((o, i) => (
        <div key={o.order_id} data-testid={`${role}-order-row-${i}`} className="grid grid-cols-[1fr_120px_120px_120px_100px] gap-4 px-5 py-4 border-b border-foreground/10 items-center">
          <div className="font-medium text-sm line-clamp-1">{o.gig_title}</div>
          <div className="text-sm text-muted-foreground">{role === "seller" ? o.buyer_name : o.seller_name}</div>
          <div className="mono font-medium">${o.package_price}</div>
          <div>
            <span className={`text-xs mono uppercase px-2 py-1 border ${statusColor(o.status)}`}>{o.status.replace("_"," ")}</span>
          </div>
          <Link to={`/order/${o.order_id}`} className="text-xs mono underline">VIEW →</Link>
        </div>
      ))}
    </div>
  );
}

function statusColor(s) {
  if (s === "completed") return "border-green-600 text-green-700";
  if (s === "delivered") return "border-primary text-primary";
  if (s === "cancelled") return "border-destructive text-destructive";
  return "border-foreground/40 text-foreground";
}
